/**
 * Main Application Component
 *
 * Purpose: Serves as the root component that handles routing, authentication,
 * and renders global UI components.
 *
 * How it works:
 * 1. Manages authentication state with useAuthSession hook
 * 2. Implements protected and unprotected routes
 * 3. Handles default redirects based on authentication status
 * 4. Renders global components (NetworkStatusIndicator, InstallAppDialog, etc.)
 *
 * Key features:
 * - Authentication-based routing protection
 * - Default route redirection
 * - Global UI components (popups, indicators)
 * - Loading state handling
 */

import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import React from "react";
import {
  ErrorBoundary,
  ErrorFallback,
  addBreadcrumb,
} from "./components/error-boundary";
import AuthLayout from "@layouts/AuthLayout";
import ChatLayout from "@layouts/ChatLayout";
import LoginPage from "@pages/LoginPage";
import ChatPage from "@pages/ChatPage";

import NewVersionPopUp from "./components/shared/NewVersionPopUp/NewVersionPopUp";
import RegisterPage from "@pages/RegisterPage";
import useAuthSession from "@/middleware/hooks/useAuthSession";
import { useNetworkStatus } from "./useNetworkStatus";
import InstallAppDialog from "./components/shared/InstallAppDialog/InstallAppDialog";
import { NetworkStatusIndicator } from "./components/shared/NetworkStatusIndicator";
import StatusBarScrim from "./components/shared/StatusBarScrim/StatusBarScrim";
import WebPushPopUp from "./components/web-push/WebPushPopUp";
import LoadingScreen from "./components/shared/LoadingScreen";
import { useTranslation } from "react-i18next";
import { useForcedReconciliation } from "./hooks/useForcedReconciliation";
import { useTcVersionCheck } from "./hooks/useTcVersionCheck";
import { useDecryptionRetryBootstrap } from "./hooks/useDecryptionRetryBootstrap";
import { useStrandedSendReconciliation } from "./hooks/useStrandedSendReconciliation";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ForgotUsernamePage from "./pages/ForgotUsernamePage";
import { featureFlags } from "./config/featureFlags";
import RecoveryKeyWizard from "./components/auth/RecoveryKey/RecoveryKeyWizard";
import ForwardMessageModal from "./components/chat/forward/ForwardMessageModal";
import VersionGate from "./components/shared/VersionGate/VersionGate";
import TcGate from "./components/shared/TcGate/TcGate";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SubscriptionCancelPage from "./pages/SubscriptionCancelPage";
import CreditsPage from "./pages/CreditsPage";
import SettingsCategoryPage from "./pages/SettingsCategoryPage";
import CreditsSuccessPage from "./pages/CreditsSuccessPage";
import TermsPage from "./pages/TermsPage";
import GuestMeetPage from "./pages/GuestMeetPage";
import { GUEST_MEET_ROUTE } from "./lib/guestInviteLink";
import { standaloneMeetRoutes } from "./pages/standaloneMeetRoutes";
import RootRouteGuard from "./pages/RootRouteGuard";
import AdminPage from "@pages/AdminPage";
import { Protected } from "./components/auth/Protected";
import { readLoginReturnPath } from "./lib/loginReturnPath";

const LegacyRedirect: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  return <Navigate to={`/${chatId}`} replace />;
};

const UnProtected = ({ children }: { children: React.JSX.Element }) => {
  const { t } = useTranslation();
  const { isLoggedIn, loading } = useAuthSession();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen title={t("loading")} />;
  }

  // Honor a validated return path carried by an auth guard (today: the
  // standalone meet routes' MeetProtected). This wrapper's redirect can win
  // the race against LoginForm's own post-login navigate, so BOTH must
  // resolve the same target or the deep link is lost. Falls back to "/"
  // (the previous behavior) whenever no state is present.
  const returnPath = readLoginReturnPath(location.state) ?? "/";
  return isLoggedIn ? <Navigate to={returnPath} replace /> : children;
};

function AppRoutes() {
  const location = useLocation();

  // Record navigation so a captured error shows how the user got there.
  // Only the pathname is logged — never the query string, which can carry
  // secrets (e.g. Stripe session ids).
  React.useEffect(() => {
    addBreadcrumb("navigation", "route change", { path: location.pathname });
  }, [location.pathname]);

  return (
    <ErrorBoundary
      scope="route"
      resetKeys={[location.key]}
      fallback={(props) => <ErrorFallback {...props} />}
    >
      <Routes>
        <Route
          element={
            <UnProtected>
              <AuthLayout />
            </UnProtected>
          }
        >
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgotPassword" element={<ForgotPasswordPage />} />
          <Route
            path="/forgotUsername"
            element={
              featureFlags.forgotUsername ? (
                <ForgotUsernamePage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Route>

        {/*
         * Root single-segment namespace, SHARED by chatIds and vanity aliases.
         * `RootRouteGuard` (the route ELEMENT) decides chat-vs-vanity by shape
         * BEFORE `ChatLayout`/`useChatRouteSync` mount. The chat branch renders
         * `ChatPage` via `ChatLayout`'s `<Outlet/>` as this route's `index`
         * child — so `ChatPage` stays an Outlet-rendered nested route exactly as
         * before. Kept as its own route (not in the layout group below) so the
         * alias branch can opt OUT of `ChatLayout` entirely.
         */}
        {/*
         * Support/admin panel. Static path so it outranks the `/:chatId?`
         * vanity/chat namespace below. `Protected` requires auth; the page
         * itself checks the caller's support/admin capability server-side and
         * shows an access-denied state otherwise.
         */}
        <Route
          path="/admin"
          element={
            <Protected>
              <AdminPage />
            </Protected>
          }
        />

        <Route path="/:chatId?" element={<RootRouteGuard />}>
          <Route index element={<ChatPage />} />
        </Route>

        <Route
          element={
            <Protected>
              <ChatLayout />
            </Protected>
          }
        >
          <Route path="/orion/:chatId" element={<LegacyRedirect />} />
          <Route path="/orion" element={<Navigate to="/" replace />} />
          <Route
            path="/subscription/success"
            element={<SubscriptionSuccessPage />}
          />
          <Route
            path="/subscription/cancel"
            element={<SubscriptionCancelPage />}
          />
          <Route path="/credits/success" element={<CreditsSuccessPage />} />
        </Route>

        {/*
         * Deep-link targets for payment panes.
         * Intentionally outside ChatLayout so useChatRouteSync doesn't
         * reset navigation state when chatId is absent.
         */}
        <Route
          path="/subscription"
          element={
            <Protected>
              <SubscriptionPage />
            </Protected>
          }
        />
        <Route
          path="/credits"
          element={
            <Protected>
              <CreditsPage />
            </Protected>
          }
        />
        {/*
         * Settings category deep link (flag settingsMiddleViews). Element-gated
         * rather than conditionally registered: with the flag off the URL
         * redirects home instead of rendering the router's blank no-match.
         */}
        <Route
          path="/settings/:category"
          element={
            featureFlags.settingsMiddleViews ? (
              <Protected>
                <SettingsCategoryPage />
              </Protected>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Public: accessible to all users regardless of auth state */}
        <Route path="/terms" element={<TermsPage />} />

        {/*
         * Guest-join page — PUBLIC and unauthenticated by design. A guest has no
         * account/session, so this route sits OUTSIDE both AuthLayout (login
         * shell) and ChatLayout (Protected app shell). It redeems the invite
         * code and drops the guest straight into the Jitsi meeting.
         *
         * The route is STATIC (no `:code` param): the secret invite code rides
         * in the URL fragment (`#<code>`), which never reaches the server, access
         * logs, the Referer header, or the navigation breadcrumb below.
         */}
        <Route path="/meet/guest" element={<GuestMeetPage />} />

        {/*
         * Standalone meetings (Product B V0) — `/meet/new` + `/meet/:meetingId`.
         * Authenticated (own guard carrying the login return-path) and DOUBLY
         * flag-gated: registers NOTHING unless `orionMeetUi` AND
         * `standaloneMeet` are both on (see standaloneMeetRoutes.tsx), so
         * prod/beta bundles ship no live surface. Static `/meet/guest` and
         * `/meet/new` outrank `/meet/:meetingId` by route specificity.
         */}
        {standaloneMeetRoutes()}
      </Routes>
    </ErrorBoundary>
  );
}

/**
 * App-wide overlays (install/version/terms/recovery/forward/web-push prompts).
 *
 * These are meant for authenticated Orion users. The public guest-join route
 * (`/meet/guest`) is a minimal, sessionless shell — a guest mid-call must not be
 * interrupted by an install banner, a forced version/terms gate, or a recovery
 * wizard. Render nothing there so the guest sees only the meeting.
 */
function GlobalOverlays() {
  const location = useLocation();
  if (location.pathname.startsWith(GUEST_MEET_ROUTE)) {
    return null;
  }

  return (
    <>
      <VersionGate />
      <TcGate />
      <NetworkStatusIndicator />
      <InstallAppDialog />
      <NewVersionPopUp />
      <RecoveryKeyWizard />
      <ForwardMessageModal />
      <WebPushPopUp />
    </>
  );
}

export default function App() {
  useNetworkStatus();
  useForcedReconciliation();
  useTcVersionCheck();
  useDecryptionRetryBootstrap();
  useStrandedSendReconciliation();

  return (
    <>
      {/* OS-chrome cosmetics for every route (login and guest meet
          included) — pure overlay, never intercepts input, so it sits
          outside the GlobalOverlays guest-route guard. */}
      <StatusBarScrim />
      <GlobalOverlays />
      {/* Used for debugging  */}
      {/* <OgTagsQueueMonitor /> */}
      <AppRoutes />
    </>
  );
}
